import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Chip from 'shared/atoms/Chip';
import CircularSpinner from 'shared/atoms/CircularSpinner';
import T from 'shared/atoms/Typography';

import Table, { CELL_TYPES, ORDER_TYPES } from 'shared/organisms/Table';

import { courseStatus } from 'shared/const';
import theme from 'shared/themes/default';

import { useWatchData } from '../../Context';
import { listUserWatchData } from '../../Context/graphql/queries';

const SpinnerWrapper = styled.div`
  padding-top: ${theme.spacing.m2};
`;

const InfoWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.s};
`;

const UsersWatchDataTableRowItem = ({
  itemId: userId,
  open,
  organizationSelectedId,
}) => {
  const { t } = useTranslation();

  const { setWatchDataError, useLazyGraphQLQuery } = useWatchData();

  const [listUserWatchDataQuery, listUserWatchDataResponse] =
    useLazyGraphQLQuery(listUserWatchData);

  useEffect(() => {
    const { error } = listUserWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listUserWatchDataResponse]);

  const watchHistory = listUserWatchDataResponse.data?.listUserWatchData || [];

  useEffect(() => {
    if (open) {
      listUserWatchDataQuery({
        variables: {
          organizationId: organizationSelectedId,
          userId,
        },
      });
    }
  }, [open]);

  const columns = [
    {
      key: 'name',
      label: t('watchData.courses'),
      fixedWidth: '30%',
      expandedSticky: true,
      type: CELL_TYPES.COMPONENT,
      render: (row) => (
        <InfoWrapper>
          {row.name}
          {row.status === courseStatus.ARCHIVED && (
            <Chip
              color={theme.palette.errorLight}
              label={t('watchData.deleted')}
              small
              theme="dark"
            />
          )}
        </InfoWrapper>
      ),
    },
    {
      key: 'progress',
      label: t('watchData.progress'),
      type: CELL_TYPES.COMPONENT,
      render: (row) => `${row.progress}%`,
    },
    {
      key: 'firstTimeWatched',
      label: t('watchData.courseStart'),
      type: CELL_TYPES.DATE,
    },
    {
      key: 'completedTime',
      label: t('watchData.courseCompleted'),
      type: CELL_TYPES.DATE,
    },
    {
      key: 'certificationTime',
      label: t('watchData.certificationDate'),
      type: CELL_TYPES.DATE,
    },
  ];

  if (listUserWatchDataResponse.loading) {
    return (
      <SpinnerWrapper>
        <CircularSpinner static />
      </SpinnerWrapper>
    );
  }

  return watchHistory.length > 0 ? (
    <Table
      ariaLabel="Expandable watch data row inner content"
      columns={columns}
      items={watchHistory}
      keyField="id"
      defaultOrder={ORDER_TYPES.DESC}
      defaultOrderBy="progress"
      isExpandedType
      isExpandedOpen={open}
    />
  ) : (
    <T color={theme.palette.lightPure} pt={theme.spacing.m2} align="center">
      {t('watchData.userHaventStartAnyCourseMessage')}
    </T>
  );
};

UsersWatchDataTableRowItem.propTypes = {
  itemId: PropTypes.string.isRequired,
  open: PropTypes.bool,
  organizationSelectedId: PropTypes.string.isRequired,
};

UsersWatchDataTableRowItem.defaultProps = {
  open: false,
};

export default UsersWatchDataTableRowItem;
